import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import SEO from '../../components/SEO';

const FaqContainer = styled.div`
  padding: 3rem 0;
`;

const FaqSection = styled.div`
  margin-bottom: 3rem;
`;

const CategoryTitle = styled.h2`
  color: var(--dark);
  margin-bottom: 1.5rem;
`;

const FaqItem = styled.div`
  margin-bottom: 1rem;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqQuestion = styled.div`
  padding: 1.25rem;
  background-color: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: var(--dark);
  }
  
  .icon {
    color: var(--primary);
    font-size: 1.25rem;
    font-weight: bold;
  }
`;

const FaqAnswer = styled.div`
  padding: ${props => props.isOpen ? '1.25rem' : '0 1.25rem'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  border-top: ${props => props.isOpen ? '1px solid #eee' : 'none'};
  
  p {
    margin-bottom: 0.75rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ContactCta = styled.div`
  background-color: var(--light);
  padding: 3rem;
  border-radius: 8px;
  text-align: center;
  
  h3 {
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const FaqPage = () => {
  const [openItems, setOpenItems] = useState({});
  
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "Baby Sleep Training FAQs | Common Questions About Dozzy Sleep Plans",
    description: "Find answers to common questions about baby sleep training, our AI-powered sleep plans, and how Dozzy helps parents establish healthy sleep habits.",
    keywords: [
      "baby sleep problems", 
      "sleep training questions", 
      "baby sleep FAQ",
      "sleep training methods",
      "infant sleep help",
      "toddler sleep issues",
      "sleep consultant FAQ"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Dozzy?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Dozzy is an AI-powered platform that helps parents create personalized sleep plans for their children. Our advanced artificial intelligence analyzes your child's specific information and combines it with guidelines from respected Australian organizations including Triple P, Rednose, and Raising Children Network."
          }
        },
        {
          "@type": "Question",
          "name": "How does Dozzy work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Dozzy works in three simple steps: 1. You complete a detailed questionnaire about your child's sleep patterns, habits, and challenges. 2. Our sophisticated AI algorithm analyzes this information and creates a personalized sleep plan by combining your child's unique data with evidence-based guidelines from trusted Australian organizations. 3. You implement the plan and can request adjustments based on your results and feedback, with our AI continuously learning to improve recommendations."
          }
        },
        {
          "@type": "Question",
          "name": "What age range does Dozzy support?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Dozzy provides sleep plans for children from newborn to 5 years of age. The recommendations are tailored to your child's specific age and developmental stage."
          }
        },
        {
          "@type": "Question",
          "name": "Can I edit my baby's profile after creating it?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, you can edit your baby's profile after creating it, but please note that there is a limit of 3 edits per profile. This limit helps ensure the consistency and accuracy of your personalized sleep plan. We recommend carefully reviewing all information before submitting your baby's profile."
          }
        },
        {
          "@type": "Question",
          "name": "What if the sleep plan doesn't work for my child?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "If you're not seeing the expected results after consistently implementing the plan for at least 5 days, you can edit your baby's profile with comments about what's happening. Our AI will then regenerate a sleep plan based on your new feedback and implementation results. Remember that you can edit your baby's profile up to 3 times, allowing for adjustments as you implement the sleep plan and observe your child's responses."
          }
        },
        {
          "@type": "Question",
          "name": "Can I purchase a sleep plan as a gift?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! You can purchase a Dozzy sleep plan as a gift for friends or family members who are parents. Simply visit our dedicated gift page at dozzy.au/gift to purchase a gift plan. The recipient will receive instructions on how to access their gift and create their child's profile. "
          }
        },
        {
          "@type": "Question",
          "name": "What about developmental changes and sleep regressions?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sleep regressions and developmental changes are normal parts of your child's growth. If your child experiences a regression or goes through a developmental leap, you can use one of your profile edits to update their information. Our AI will then adjust the sleep plan to accommodate these changes and provide appropriate guidance for the current developmental stage."
          }
        }
      ]
    }
  };
  
  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  const faqCategories = [
    {
      id: 'general',
      title: 'General Questions',
      items: [
        {
          id: 'what-is',
          question: 'What is Dozzy?',
          answer: (
            <div>
              <p>Dozzy is an AI-powered platform that helps parents create personalized sleep plans for their children. Our advanced artificial intelligence analyzes your child's specific information and combines it with guidelines from respected Australian organizations including Triple P, Rednose, and Raising Children Network.</p>
              <p>Our platform bridges the gap between affordable AI guidance and professional sleep consulting services, providing customized sleep programs that are tailored to your child's unique needs, age, and sleep challenges.</p>
            </div>
          )
        },
        {
          id: 'how-works',
          question: 'How does Dozzy work?',
          answer: (
            <div>
              <p>Dozzy works in three simple steps:</p>
              <p>1. You complete a detailed questionnaire about your child's sleep patterns, habits, and challenges.</p>
              <p>2. Our sophisticated AI algorithm analyzes this information and creates a personalized sleep plan by combining your child's unique data with evidence-based guidelines from trusted Australian organizations.</p>
              <p>3. You implement the plan and can request adjustments based on your results and feedback, with our AI continuously learning to improve recommendations.</p>
            </div>
          )
        },
        {
          id: 'age-range',
          question: 'What age range does Dozzy support?',
          answer: (
            <p>Dozzy provides sleep plans for children from newborn to 5 years of age. The recommendations are tailored to your child's specific age and developmental stage.</p>
          )
        }
      ]
    },
    {
      id: 'resources',
      title: 'Our Resources & Approach',
      items: [
        {
          id: 'resources-used',
          question: 'What resources does Dozzy use to create sleep plans?',
          answer: (
            <div>
              <p>Dozzy creates personalized sleep plans by combining our advanced AI technology with publicly available guidelines and resources from respected Australian organizations. We reference valuable information from Triple P (Positive Parenting Program), Red Nose Australia, Raising Children Network, and Tresillian to inform our approach.</p>
              <p>Our AI system analyzes your child's unique sleep patterns and needs, then applies relevant guidance from these trusted resources to create a customized plan specifically for your family situation.</p>
            </div>
          )
        },
        {
          id: 'organization-relationship',
          question: 'Is Dozzy affiliated with Triple P, Red Nose Australia, Raising Children Network, or Tresillian?',
          answer: (
            <div>
              <p>While we greatly respect and value the work of these organizations, Dozzy is an independent service and is not affiliated with, endorsed by, or partnered with Triple P, Red Nose Australia, Raising Children Network, or Tresillian.</p>
              <p>We've developed our AI system to incorporate publicly available guidelines and resources from these respected organizations, combining their valuable insights with our technology to create personalized sleep plans that are tailored to each child's unique needs.</p>
            </div>
          )
        },
        {
          id: 'ai-approach',
          question: "How does Dozzy's AI create personalized sleep plans?",
          answer: (
            <div>
              <p>Our AI system works by analyzing the specific information you provide about your child's sleep patterns, habits, temperament, and challenges. It then processes this data alongside guidelines from trusted sleep resources to create a personalized plan.</p>
              <p>What makes our approach unique is how we combine the best of both worlds: the personalization capabilities of AI technology with the trusted guidance found in respected sleep resources. This allows us to create sleep plans that are both evidence-informed and specifically tailored to your child's individual needs.</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'parent-support',
      title: 'Parent Support & Mental Health',
      items: [
        {
          id: 'mental-health-support',
          question: 'Where can I get mental health support as a parent?',
          answer: (
            <div>
              <p>Parenting can be challenging, especially when dealing with sleep issues. If you're feeling overwhelmed, anxious, or struggling with your mental health, please reach out to these valuable Australian support services:</p>
              <ul>
                <li><strong><a href="https://www.ruok.org.au/" target="_blank" rel="noopener noreferrer">R U OK?</a></strong> - Suicide prevention charity that reminds people to ask "Are you OK?" and provides resources for supporting others.</li>
                <li><strong><a href="https://www.beyondblue.org.au/" target="_blank" rel="noopener noreferrer">Beyond Blue</a></strong> - 1300 22 4636 - Support for anxiety, depression and suicide prevention.</li>
                <li><strong><a href="https://www.gidgetfoundation.org.au/" target="_blank" rel="noopener noreferrer">Gidget Foundation</a></strong> - Support for perinatal anxiety and depression.</li>
                <li><strong><a href="https://www.panda.org.au/" target="_blank" rel="noopener noreferrer">PANDA</a></strong> - 1300 726 306 - National helpline for individuals and families affected by perinatal anxiety and depression.</li>
                <li><strong><a href="https://www.lifeline.org.au/" target="_blank" rel="noopener noreferrer">Lifeline</a></strong> - 13 11 14 - 24/7 crisis support and suicide prevention services.</li>
              </ul>
              <p>Remember, seeking help is a sign of strength, not weakness. Your mental health matters, and it's important for both you and your child.</p>
            </div>
          )
        },
        {
          id: 'sleep-deprivation',
          question: 'How can I cope with sleep deprivation as a parent?',
          answer: (
            <div>
              <p>Sleep deprivation is a common challenge for parents. Here are some strategies that may help:</p>
              <ul>
                <li><strong>Share the load</strong> - If possible, take turns with your partner for night wakings.</li>
                <li><strong>Sleep when your baby sleeps</strong> - Try to rest when your child naps during the day.</li>
                <li><strong>Ask for help</strong> - Don't hesitate to ask family or friends to watch your child while you take a nap.</li>
                <li><strong>Simplify your routine</strong> - Focus on essential tasks and let go of perfectionism.</li>
                <li><strong>Practice self-care</strong> - Even small moments of self-care can help restore your energy.</li>
                <li><strong>Seek professional support</strong> - If sleep deprivation is severely affecting your wellbeing, talk to your healthcare provider.</li>
              </ul>
              <p>If you're feeling overwhelmed, please reach out to the mental health resources listed above. You're not alone in this journey.</p>
            </div>
          )
        },
        {
          id: 'partner-support',
          question: 'How can I get my partner more involved in our sleep plan?',
          answer: (
            <div>
              <p>Implementing a sleep plan is most effective when all caregivers are on the same page. Here are some tips for involving your partner:</p>
              <ul>
                <li><strong>Share the sleep plan</strong> - Make sure your partner has read and understands the sleep plan.</li>
                <li><strong>Discuss roles</strong> - Have an open conversation about how you'll divide responsibilities.</li>
                <li><strong>Be consistent together</strong> - Consistency from both parents helps children adapt more quickly.</li>
                <li><strong>Support each other</strong> - Acknowledge that sleep training can be emotionally challenging and provide mutual support.</li>
                <li><strong>Communicate regularly</strong> - Check in with each other about what's working and what isn't.</li>
              </ul>
              <p>If you're finding it difficult to align on parenting approaches, consider seeking support from a relationship counselor who specializes in family dynamics.</p>
            </div>
          )
        }
      ]
    },
    {
      id: 'plans',
      title: 'Sleep Plans & Pricing',
      items: [
        {
          id: 'plan-options',
          question: 'What plans does Dozzy offer?',
          answer: (
            <div>
              <p>Dozzy offers two simple plan options:</p>
              <p><strong>Basic Plan ($49):</strong> For families with one child, providing a personalized AI-generated sleep plan.</p>
              <p><strong>Family Plan ($79):</strong> For families with up to two children, providing personalized AI-generated sleep plans for each child.</p>
              <p>Both plans include the same comprehensive features. The only difference is the number of children you can create sleep plans for.</p>
              <p>All our plans are one-time payments, not subscriptions.</p>
            </div>
          )
        },
        {
          id: 'refund',
          question: 'Do you offer refunds?',
          answer: (
            <p>Yes, we offer a 7-day money-back guarantee. If you're not satisfied with your sleep plan, you can request a refund within 7 days of purchase. However, due to the costs associated with generating personalized sleep programs, refunds are limited to 70% of the purchase price.</p>
          )
        },
        {
          id: 'upgrade',
          question: 'Can I upgrade my plan later?',
          answer: (
            <p>Yes, you can upgrade from a Basic Plan to a Family Plan at any time. You'll only pay the difference between the plans. To upgrade, simply log into your account and select the "Upgrade Plan" option from your dashboard.</p>
          )
        },
        {
          id: 'gift',
          question: 'Can I purchase a sleep plan as a gift?',
          answer: (
            <p>Yes! You can purchase a Dozzy sleep plan as a gift for friends or family members who are parents. Simply visit our dedicated gift page at <Link to="/gift">dozzy.au/gift</Link> to purchase a gift plan. The recipient will receive instructions on how to access their gift and create their child's profile. </p>
          )
        }
      ]
    },
    {
      id: 'implementation',
      title: 'Implementation & Results',
      items: [
        {
          id: 'how-long',
          question: 'How long does it take to see results?',
          answer: (
            <p>Most families start seeing improvements in their child's sleep within 3-7 days of consistently implementing the sleep plan. However, every child is different, and some may take longer to adjust to new sleep routines. Consistency is key to successful implementation.</p>
          )
        },
        {
          id: 'adjustments',
          question: "What if the sleep plan doesn't work for my child?",
          answer: (
            <div>
              <p>If you're not seeing the expected results after consistently implementing the plan for at least 5 days, you can edit your baby's profile with comments about what's happening. Our AI will then regenerate a sleep plan based on your new feedback and implementation results.</p>
              <p>Remember that you can edit your baby's profile up to 3 times, allowing for adjustments as you implement the sleep plan and observe your child's responses. Each edit will trigger a regeneration of the sleep plan with refined recommendations.</p>
            </div>
          )
        },
        {
          id: 'consistency',
          question: 'How important is consistency when implementing the sleep plan?',
          answer: (
            <p>Consistency is crucial for successful sleep training. Children thrive on routine and predictability, so following the sleep plan consistently helps them learn new sleep habits more quickly. Inconsistent implementation can confuse your child and delay progress.</p>
          )
        },
        {
          id: 'developmental-changes',
          question: 'What about developmental changes and sleep regressions?',
          answer: (
            <p>Sleep regressions and developmental changes are normal parts of your child's growth. If your child experiences a regression or goes through a developmental leap, you can use one of your profile edits to update their information. Our AI will then adjust the sleep plan to accommodate these changes and provide appropriate guidance for the current developmental stage.</p>
          )
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Questions',
      items: [
        {
          id: 'subscription',
          question: 'Is this a subscription or a one-time payment?',
          answer: (
            <p>All our plans are <strong>one-time payments</strong>, not subscriptions. You pay once and get lifetime access to your personalized sleep plan(s). There are no recurring charges or hidden fees. This includes the ability to make adjustments to your plan as your child grows and their sleep needs change.</p>
          )
        },
        {
          id: 'profile-edits',
          question: 'Can I edit my baby\'s profile after creating it?',
          answer: (
            <p>Yes, you can edit your baby's profile after creating it, but please note that there is a limit of 3 edits per profile. This limit helps ensure the consistency and accuracy of your personalized sleep plan. We recommend carefully reviewing all information before submitting your baby's profile. If you need to make significant changes after reaching the edit limit, please contact our support team.</p>
          )
        },
        {
          id: 'data-security',
          question: 'How is my data secured?',
          answer: (
            <p>We take data security seriously. All your information is encrypted and stored securely on AWS servers. We comply with the Privacy Act 1988 (Cth) and Australian Privacy Principles (APPs). For more information, please see our Privacy Policy.</p>
          )
        },
        {
          id: 'device-compatibility',
          question: 'What devices can I use Dozzy on?',
          answer: (
            <p>Dozzy is a web-based application that works on any modern browser. You can access it from your desktop, laptop, tablet, or smartphone.</p>
          )
        }
      ]
    }
  ];
  
  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/faq"
        ogType="website"
      />
      
      {/* Page Header */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container py-2">
          <h1>Frequently Asked Questions</h1>
          <p className="mx-auto" style={{ maxWidth: '700px' }}>
            Find answers to common questions about Dozzy and our sleep plans
          </p>
        </div>
      </section>
      
      <div className="container">
        <FaqContainer>
          {faqCategories.map(category => (
            <FaqSection key={category.id}>
              <CategoryTitle>{category.title}</CategoryTitle>
              {category.items.map(item => (
                <FaqItem key={item.id}>
                  <FaqQuestion onClick={() => toggleItem(item.id)}>
                    <h3>{item.question}</h3>
                    <span className="icon">{openItems[item.id] ? '−' : '+'}</span>
                  </FaqQuestion>
                  <FaqAnswer isOpen={openItems[item.id]}>
                    {item.answer}
                  </FaqAnswer>
                </FaqItem>
              ))}
            </FaqSection>
          ))}
          
          <ContactCta>
            <h3>Still have questions?</h3>
            <p>We're here to help! If you couldn't find the answer to your question, please don't hesitate to reach out to our support team.</p>
            <Button to="/contact" variant="primary">Contact Us</Button>
          </ContactCta>
        </FaqContainer>
      </div>
    </>
  );
};

export default FaqPage;
